import React, { useState, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import './ClientPlugins.css';
import {
    Container,
    Row,
    Form,
    InputGroup,
    Label,
    Input,
    Button,
} from 'reactstrap';
import { actions } from '../../../../../../context';
import ClientPluginsInfo from './ClientPluginsInfo';
import StatusCard from './ClientStatus/StatusCard';
import hasPermission from '../../../../../Shared/hasPermission';
import { Facebook } from 'react-spinners-css';
import MissionControlStatusDetails from './MissionControlStatusDetails';
import MissionControlLogsButton from './MissionControlLogsButton';

const ClientPlugins = () => {
    const dispatch = useDispatch();

    const [clientManifest, setClientManifest] = useState('');
    const [missionControlHeartbeat, setMissionControlHeartbeat] =
        useState(null);
    const [manifestLoading, setManifestLoading] = useState(false);
    const [heartbeatLoading, setHeartbeatLoading] = useState(false);
    const [pushManifestResponse, setPushManifestResponse] = useState({
        isSuccess: null,
        message: 'Ready to push manifest to instance',
    });

    const instanceId = useSelector(
        (state) => state.practice?.practice?.instanceId
    );

    useEffect(() => {
        setManifestLoading(true);
        setHeartbeatLoading(true);
        setPushManifestResponse({
            isSuccess: null,
            message: 'Ready to push manifest to instance',
        });
        dispatch(actions.clientPlugins.fetchClientManifest(instanceId))
            .then((res) => {
                setClientManifest(res ?? '');
            })
            .then(() => setManifestLoading(false));
        dispatch(actions.clientPlugins.fetchMissionControlStatus(instanceId))
            .then((res) => setMissionControlHeartbeat(res))
            .then(() => setHeartbeatLoading(false));
    }, [dispatch, instanceId]);

    const refreshManifest = () => {
        dispatch(actions.clientPlugins.fetchClientManifest(instanceId)).then(
            (res) => {
                setClientManifest(res);
            }
        );
        dispatch(
            actions.clientPlugins.fetchMissionControlStatus(instanceId)
        ).then((res) => setMissionControlHeartbeat(res));
    };

    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );

    const canEditPlugins = hasPermission(['ClientPluginsEdit'], permissions);

    return (
        <>
            <Container className="sc-glass text-14 text-light sc-rounded mb-4">
                <Row>
                    <div className="col-12 col-md-12 col-lg-12 col-xl-6 p-2 text-center">
                        <MissionControlStatusDetails
                            missionControlHeartbeat={missionControlHeartbeat}
                            targetManifestVersion={
                                clientManifest.missionControlVersion
                            }
                            refreshManifest={() => refreshManifest()}
                        />
                    </div>
                    <div className="col-12 col-md-12 col-lg-12 col-xl-3 p-2 text-center">
                        <MissionControlLogsButton instanceId={instanceId} />
                    </div>
                    <div className="col-12 col-md-12 col-lg-12 col-xl-3 p-2 text-center">
                        <StatusCard
                            classes={getManifestClass(pushManifestResponse)}
                            heading="Push Manifest"
                            text={
                                clientManifest === ''
                                    ? 'No Client Manifest found'
                                    : pushManifestResponse.message
                            }
                            dropdownItems={[
                                {
                                    itemAction: () =>
                                        triggerPushManifest(
                                            dispatch,
                                            instanceId,
                                            setPushManifestResponse
                                        ),
                                    itemText: 'Push Manifest',
                                },
                            ]}
                            hasPermission={canEditPlugins}
                        />
                    </div>
                </Row>
            </Container>
            {manifestLoading ||
            heartbeatLoading ||
            missionControlHeartbeat === null ? (
                <h2 className="m-4 text-center">
                    <Facebook color="#eee" />
                </h2>
            ) : (
                <Container className="text-14 text-light">
                    <Row>
                        <ClientPluginsInfo
                            instanceId={instanceId}
                            canEditPlugins={canEditPlugins}
                            clientPlugins={clientManifest.plugins || []}
                            pluginStatuses={
                                missionControlHeartbeat.pluginStatuses
                            }
                            refreshManifest={refreshManifest}
                        />
                    </Row>
                </Container>
            )}
        </>
    );
};

const getManifestClass = (pushManifestResponse) => {
    if (pushManifestResponse.isSuccess === null) return 'bg-secondary';
    if (pushManifestResponse.isSuccess) return 'bg-success';

    return 'bg-danger';
};

const triggerPushManifest = async (
    dispatch,
    instanceId,
    setPushManifestStatus
) => {
    setPushManifestStatus({
        isSuccess: null,
        message: 'Sending request...',
    });
    dispatch(
        await actions.clientPlugins.triggerPushManifestToPractice(instanceId)
    ).then((res) => setPushManifestStatus(res));
};

export default ClientPlugins;
